import * as React from 'react';

import { Button } from '../../ui/button';
import { Calendar } from '../../ui/calendar';
import { Dropdown as RdpDropdown, type DropdownProps } from 'react-day-picker';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { EtaTrigger } from './EtaTrigger';
import { HourInput } from './HourInput';
import { DEFAULT_HOUR, MAX_HOUR, MAX_WEEKS_AHEAD } from './constants';

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);
const addDays = (d: Date, days: number) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate() + days);

// The caption dropdowns list ONLY the months/years inside the selectable
// window. react-day-picker builds every month of the year and merely disables
// the out-of-range ones, so drop the disabled options before its own Dropdown.
function WindowedDropdown(props: DropdownProps) {
  return <RdpDropdown {...props} options={props.options?.filter((o) => !o.disabled)} />;
}

const CALENDAR_COMPONENTS = { Dropdown: WindowedDropdown };

/** Show the year dropdown only when the window crosses into another year;
 *  otherwise the month dropdown alone is enough. */
function captionLayoutFor(spansYears: boolean): 'dropdown' | 'dropdown-months' {
  if (spansYears) return 'dropdown';
  return 'dropdown-months';
}

/** Viewer-local "15 Aug 2026" — the time-step header. */
const formatDayLabel = (d: Date) =>
  d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

const STEP_HEADING = 'text-caption-medium text-muted-foreground tracking-wide uppercase';

export interface EtaPickerProps {
  /** Current ETA (ISO-8601 UTC) or null/undefined when unset. */
  value?: string | null;
  /**
   * Persist the new ETA (absolute UTC instant), or null to clear. May be async;
   * the picker shows a busy state and closes on success, staying open if it throws.
   */
  onChange: (isoUtc: string | null) => Promise<void> | void;
  disabled?: boolean;
  className?: string;
  /**
   * Custom popover trigger (a host `<button>` element so Radix can attach its
   * ref) replacing the default EtaTrigger pill — e.g. the review-state upload
   * date doubling as the "set the next revision's ETA" entry point.
   */
  trigger?: React.ReactElement;
}

/**
 * Admin ETA editor: a two-step popover (delivery date, then hour-only 24h time).
 * Picked in local time and emitted as a UTC instant. Selectable range is the
 * closed interval [now, now + MAX_WEEKS_AHEAD] — the BE enforces the same
 * bound at instant granularity, so the hour picker clamps on the two edge
 * days to match (see minHour/maxHour below).
 */
export function EtaPicker({ value, onChange, disabled, className, trigger }: EtaPickerProps) {
  const [open, setOpen] = React.useState(false);
  const [step, setStep] = React.useState<0 | 1>(0);
  const [busy, setBusy] = React.useState(false);
  const [day, setDay] = React.useState<Date | null>(null);
  const [hour, setHour] = React.useState(DEFAULT_HOUR);
  const [view, setView] = React.useState<Date>(() => startOfMonth(new Date()));

  const now = React.useMemo(() => new Date(), []);
  const today = React.useMemo(() => startOfDay(now), [now]);
  // Furthest future date selectable — mirrors the BE's domain.MaxETAHorizon.
  const maxDate = React.useMemo(() => addDays(today, MAX_WEEKS_AHEAD * 7), [today]);

  // The BE validates against the exact INSTANT (now, now+MAX_WEEKS_AHEAD),
  // not day boundaries — so on the two edge days the hour picker must clamp
  // too, or a same-day-but-earlier-hour (or horizon-day-but-later-hour)
  // selection would pass the calendar's day-level check yet still get
  // rejected by the BE at save time. Both edges share the same cutoff hour:
  // "42 days from now" falls on the same hour-of-day as "now".
  const minHour = day && day.getTime() === today.getTime() ? now.getHours() : 0;
  const maxHour = day && day.getTime() === maxDate.getTime() ? now.getHours() : MAX_HOUR;
  const clampHour = (h: number) => Math.min(maxHour, Math.max(minHour, h));

  // Seed the draft on the OPEN transition only — not on later `value` changes,
  // which would wipe an in-progress selection if the row refetched while the
  // popover was open. `value` is read fresh via the closure at open time.
  // Done from the open/close event handler rather than an effect so the seed
  // commits in the same render pass Radix uses to open the popover.
  const handleOpenChange = (nextOpen: boolean) => {
    if (disabled) return;
    if (nextOpen) {
      const base = value ? new Date(value) : null;
      setStep(0);
      setDay(base ? startOfDay(base) : null);
      setHour(base ? base.getHours() : DEFAULT_HOUR);
      setView(startOfMonth(base ?? new Date()));
    }
    setOpen(nextOpen);
  };

  const commit = async (iso: string | null) => {
    setBusy(true);
    try {
      await onChange(iso);
      setOpen(false);
    } catch (err) {
      // Leave the popover open so the admin can retry; the mutation layer toasts.
      console.error('[EtaPicker] failed to save ETA', { value, err });
    } finally {
      setBusy(false);
    }
  };

  const handleSave = () => {
    if (!day) return;
    // Local wall-clock → absolute UTC instant.
    const local = new Date(day.getFullYear(), day.getMonth(), day.getDate(), hour, 0, 0, 0);
    void commit(local.toISOString());
  };

  // Calendar month-nav bounds, widened to keep a seeded out-of-range date
  // selectable (e.g. an ETA set before the 6-week cap existed).
  const startMonth = startOfMonth(day && day < today ? day : today);
  const endMonth = startOfMonth(day && day > maxDate ? day : maxDate);
  const captionLayout = captionLayoutFor(startMonth.getFullYear() !== endMonth.getFullYear());

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        {trigger ?? <EtaTrigger value={value} disabled={disabled} className={className} />}
      </PopoverTrigger>

      <PopoverContent align="start" className="w-[290px] p-3">
        {step === 0 ? (
          <div className="flex flex-col gap-3">
            <div className={STEP_HEADING}>Delivery date</div>
            <Calendar
              mode="single"
              captionLayout={captionLayout}
              selected={day ?? undefined}
              onSelect={(d) => setDay(d ?? null)}
              month={view}
              onMonthChange={setView}
              startMonth={startMonth}
              endMonth={endMonth}
              disabled={[{ before: today }, { after: maxDate }]}
              components={CALENDAR_COMPONENTS}
              className="p-0"
            />
            <div className="flex gap-2 pt-1">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="default"
                size="sm"
                className="flex-1"
                disabled={!day}
                onClick={() => {
                  // Re-clamp before showing the hour step — a day change can
                  // leave a previously-valid hour (or the DEFAULT_HOUR seed)
                  // outside the new day's [minHour, maxHour] window.
                  setHour(clampHour);
                  setStep(1);
                }}
              >
                Next
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <div className={STEP_HEADING}>Delivery time · {day ? formatDayLabel(day) : ''}</div>
            <HourInput hour={hour} onChange={setHour} minHour={minHour} maxHour={maxHour} />
            <div className="flex gap-2 pt-1">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={() => setStep(0)}
              >
                Back
              </Button>
              <Button
                type="button"
                variant="default"
                size="sm"
                className="flex-1"
                loading={busy}
                onClick={handleSave}
              >
                Save ETA
              </Button>
            </div>
            {value ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive"
                disabled={busy}
                onClick={() => void commit(null)}
              >
                Clear ETA
              </Button>
            ) : null}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
